import { state } from '../lib/state.js';
import { loadLineCalculatorSettings, saveLineCalculatorSettings } from '../services/repository.js';
import { startCountryBrowserlessJob } from '../services/browserless-country-dispatch.js';
import { els } from '../app/dom.js';
import { requireAuthenticatedSession } from '../app/auth.js';
import { setStatus, esc, sectionDistanceKm, formatDuration } from '../lib/app-helpers.js';
import { prepareSectionMetrics } from './common.js';

const A1_AMBERONE_STATIONS = [
  'Rusocin','Stanisławie','Swarożyn','Pelplin','Kopytkowo','Warlubie',
  'Nowe Marzy','Grudziądz','Lisewo','Turzno','Lubicz','Nowa Wieś',
];
const MOTORWAY_CALCULATOR_KEYS = new Set([
  'a1-amberone-pl','a2-awsa-pl','a4-sam-pl',
  'hac-hr','azm-hr','bina-istra-hr',
  'autostrade-it','autoroutes-fr',
]);
let pendingPolishConcessionSection = null;
let polishConcessionUi = null;

function normalizeHacValue(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase();
}

export function inferHacRoadGroup(section, existingSettings = {}) {
  const points = Array.isArray(section?.points) ? section.points : [];
  const entryName = String(
    section?.tollEntryName
    || section?.entryName
    || points[0]?.name
    || existingSettings?.entryName
    || ''
  ).trim();
  const exitName = String(
    section?.tollExitName
    || section?.exitName
    || points.at(-1)?.name
    || existingSettings?.exitName
    || ''
  ).trim();

  const roadCode = String(section?.roadCode || existingSettings?.roadCode || '').toUpperCase();
  const description = normalizeHacValue([
    section?.displayName,
    section?.operatorName,
    roadCode,
  ].filter(Boolean).join(' '));

  // 1. Najpewniejsze źródło: kod/nazwa sekcji wykrytej przez route-service.
  if (description.includes('bregana')) return 'bregana';
  if (/\bA4\b/i.test(roadCode) || /\ba4\b/.test(description)) return 'a4';
  if (/\bA7\b/i.test(roadCode) || /\ba7\b/.test(description) || description.includes('rupa')) return 'rupa';
  if (/\bA5\b/i.test(roadCode) || /\ba5\b/.test(description)) return 'a3';
  if (
    /\bA1\b/i.test(roadCode)
    || /\bA6\b/i.test(roadCode)
    || /\bA10\b/i.test(roadCode)
    || /\bD425\b/i.test(roadCode)
    || /\ba1\b/.test(description)
    || /\ba6\b/.test(description)
    || /\ba10\b/.test(description)
    || /\bd425\b/.test(description)
  ) return 'a1';

  const entry = normalizeHacValue(entryName);
  const exit = normalizeHacValue(exitName);

  const inPair = set => set.has(entry) && set.has(exit);

  // A4 Zagreb–Goričan
  const a4 = new Set([
    'gorican','cakovec','varazdin','varazdinske toplice','varaz toplice',
    'novi marof','breznicki hum','brez hum','komin','sveta helena','sveta',
  ]);
  if (inPair(a4)) return 'a4';

  // A7 Rupa
  const rupa = new Set(['rupa','rijeka','krizisce']);
  if (inPair(rupa)) return 'rupa';

  // A3 Bregana ma osobny cennik.
  const bregana = new Set(['bregana','zagreb zapad']);
  if (inPair(bregana)) return 'bregana';

  // A3/A5 — wschodnia sieć HAC.
  const a3 = new Set([
    'zagreb istok','ivanic grad','kriz','popovaca','kutina','novska',
    'okucani','nova gradiska','slavonski brod zapad','slavonski brod istok',
    'zupanja','lipovac','svilaj','dakovo','cepin','osijek','beli manastir',
  ]);
  if (inPair(a3)) return 'a3';

  // A1/A6/A10/D425 — m.in. Zagreb → Zadar, Split i Rijeka/Grobnik.
  const a1 = new Set([
    'zagreb','zdencina','jastrebarsko','karlovac','novigrad','bosiljevo',
    'vrbovsko','ravna gora','delnice','vrata','ostrovica',
    'rijeka','rijeka grobnik',
    'ogulin','zuta lokva','brinje','otocac','perusic','gospic',
    'gornja ploca','sveti rok','rovanjska','posedarje',
    'zadar','zadar istok','zadar centar',
    'benkovac','vodice','skradin','sibenik','podi','vucevica',
    'prgomet','danilo','bisko','split','blato na cetini','sestanovac',
    'zagvozd','vrgorac','ravca','carapine','karamatici',
  ]);
  if (inPair(a1)) return 'a1';

  // A3 jest niejednoznaczne przy Breganie, dlatego dopiero po sprawdzeniu stacji.
  if (/\bA3\b/i.test(roadCode) || /\ba3\b/.test(description)) return 'a3';

  // 2. Zapisana wartość jest fallbackiem, a nie źródłem ważniejszym od aktualnej sekcji.
  const supplied = String(section?.roadGroup || existingSettings?.roadGroup || '').trim().toLowerCase();
  return ['a1','a3','a4','bregana','rupa'].includes(supplied) ? supplied : '';
}

function motorwaySectionRouteSettings(section) {
  const points = Array.isArray(section?.points) ? section.points : [];
  return {
    entryName:String(
      section?.tollEntryName
      || section?.entryName
      || points[0]?.name
      || ''
    ).trim(),
    exitName:String(
      section?.tollExitName
      || section?.exitName
      || points.at(-1)?.name
      || ''
    ).trim(),
    roadGroup:String(section?.calculatorKey === 'hac-hr' ? inferHacRoadGroup(section) : (section?.roadGroup || '')).trim(),
    roadCode:String(section?.roadCode || '').trim(),
    operatorName:String(section?.operatorName || '').trim(),
    displayName:String(section?.displayName || '').trim(),
    sectionOrder:Number(section?.order || 0),
  };
}

export async function syncMotorwaySectionSettingsToLine(route) {
  const lineNumber = String(route?.lineNumber || '').trim();
  if (!lineNumber || !state.organizationId) return;

  const sections = (route?.sections || []).filter(section => (
    MOTORWAY_CALCULATOR_KEYS.has(String(section?.calculatorKey || ''))
  ));

  for (const section of sections) {
    const calculatorKey = String(section.calculatorKey || '');
    const routeSettings = motorwaySectionRouteSettings(section);

    // Wjazd/wyjazd i grupa autostrady pochodzą z zapisanej sekcji linii,
    // a nie z osobnego ręcznego formularza.
    let existing = {};
    try {
      existing = await loadLineCalculatorSettings(
        state.organizationId,
        lineNumber,
        calculatorKey
      ) || {};
    } catch (error) {
      console.warn(`[D24 AUTOSTRADA] Nie udało się wczytać ustawień ${calculatorKey}:`, error);
    }

    const sectionKey = String(Number(section.order || 0));
    const sameCalculatorCount = sections.filter(item =>
      String(item?.calculatorKey || '') === calculatorKey
    ).length;

    const existingSectionRoute =
      existing.sectionRoutes && typeof existing.sectionRoutes === 'object'
        ? (existing.sectionRoutes[sectionKey] || {})
        : {};

    // Zapisane ustawienia tej konkretnej linii/sekcji mają pierwszeństwo.
    // Ponowne wyznaczenie lub zapis trasy nie może przywracać wartości
    // wykrytych automatycznie, jeśli użytkownik wcześniej je zatwierdził.
    const legacyRouteFallback = sameCalculatorCount === 1 ? existing : {};
    const savedRoute = {
      ...legacyRouteFallback,
      ...existingSectionRoute,
    };

    const persistentRouteSettings = {
      ...routeSettings,
      entryName:String(savedRoute.entryName || routeSettings.entryName || '').trim(),
      exitName:String(savedRoute.exitName || routeSettings.exitName || '').trim(),
      roadGroup:String(savedRoute.roadGroup || routeSettings.roadGroup || '').trim(),
      roadCode:String(savedRoute.roadCode || routeSettings.roadCode || '').trim(),
      operatorName:String(savedRoute.operatorName || routeSettings.operatorName || '').trim(),
    };

    const sectionRoutes = {
      ...(existing.sectionRoutes && typeof existing.sectionRoutes === 'object'
        ? existing.sectionRoutes
        : {}),
      [sectionKey]:persistentRouteSettings,
    };

    const next = {
      ...existing,
      sectionRoutes,
    };

    if (sameCalculatorCount === 1) Object.assign(next, persistentRouteSettings);

    // AZM jest w runnerze ustawiony na stałe jako klasa III.
    if (calculatorKey === 'azm-hr') {
      next.vehicleCategory = existing.vehicleCategory || 'III';
    }

    await saveLineCalculatorSettings(
      state.organizationId,
      lineNumber,
      calculatorKey,
      next
    );
  }
}



function ensurePolishConcessionDialog(){
  if(polishConcessionUi)return polishConcessionUi;
  const style=document.createElement('style');
  style.id='d24-polish-concession-dialog-style';
  style.textContent=`
    #d24PolishConcessionDialog{position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(15,23,42,.58)}
    #d24PolishConcessionDialog.hidden{display:none}
    #d24PolishConcessionDialog .d24-pl-dialog{position:relative;width:min(680px,100%);max-height:90vh;overflow:auto;padding:24px;background:#fff;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.3);color:#172033}
    #d24PolishConcessionDialog h2{margin:0;color:#b42318;font-size:24px}
    #d24PolishConcessionDialog .d24-pl-route{margin:8px 0 18px;padding:11px 13px;background:#fff4ed;border:1px solid #ffd8c2;border-radius:9px;color:#5d2f1f;line-height:1.45}
    #d24PolishConcessionDialog .d24-pl-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
    #d24PolishConcessionDialog label{display:flex;flex-direction:column;gap:6px;font-weight:700;color:#344054}
    #d24PolishConcessionDialog input,#d24PolishConcessionDialog select{min-height:42px;padding:9px 10px;border:1px solid #cbd5e1;border-radius:8px;background:#fff;font:inherit;color:#172033}
    #d24PolishConcessionDialog .d24-pl-note{margin-top:14px;padding:10px 12px;border:1px solid #d0d5dd;border-radius:9px;background:#f8fafc;color:#475467;font-size:12px;line-height:1.45}
    #d24PolishConcessionDialog .d24-pl-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:20px}
    #d24PolishConcessionDialog button{border:0;border-radius:8px;padding:10px 16px;font-weight:700;cursor:pointer}
    #d24PolishConcessionDialog .d24-pl-cancel{background:#e8edf3;color:#26364a}
    #d24PolishConcessionDialog .d24-pl-submit{background:#b42318;color:#fff}
    #d24PolishConcessionDialog .d24-pl-close{position:absolute;right:14px;top:12px;background:transparent;color:#667085;font-size:24px;padding:4px 8px}
    @media(max-width:650px){#d24PolishConcessionDialog .d24-pl-grid{grid-template-columns:1fr}}
  `;
  document.head.appendChild(style);

  const dialog=document.createElement('div');
  dialog.id='d24PolishConcessionDialog';
  dialog.className='hidden';
  dialog.setAttribute('aria-hidden','true');
  dialog.innerHTML=`
    <section class="d24-pl-dialog" role="dialog" aria-modal="true" aria-labelledby="d24PolishConcessionTitle">
      <button type="button" class="d24-pl-close" aria-label="Zamknij">×</button>
      <h2 id="d24PolishConcessionTitle">Dodatkowa opłata autostradowa</h2>
      <div class="d24-pl-route" id="d24PolishConcessionRoute"></div>
      <form id="d24PolishConcessionForm">
        <div class="d24-pl-grid">
          <label id="d24PolishConcessionEntryTextField">Punkt wjazdu
            <input id="d24PolishConcessionEntry" required autocomplete="off">
          </label>
          <label id="d24PolishConcessionExitTextField">Punkt wyjazdu
            <input id="d24PolishConcessionExit" required autocomplete="off">
          </label>
          <label id="d24PolishConcessionEntrySelectField" class="hidden">Węzeł wjazdu AmberOne
            <select id="d24PolishConcessionEntrySelect"></select>
          </label>
          <label id="d24PolishConcessionExitSelectField" class="hidden">Węzeł wyjazdu AmberOne
            <select id="d24PolishConcessionExitSelect"></select>
          </label>
          <label>Kategoria pojazdu
            <select id="d24PolishConcessionCategory" required>
              <option value="2">Kategoria 2 — 2 osie, koła bliźniacze / przyczepa</option>
              <option value="3">Kategoria 3 — autobus lub pojazd 3-osiowy</option>
              <option value="4">Kategoria 4 — więcej niż 3 osie</option>
              <option value="5">Kategoria 5 — pojazd nienormatywny</option>
            </select>
          </label>
          <label>Kierunek przejazdu
            <select id="d24PolishConcessionDirection">
              <option value="one-way">Przejazd w jedną stronę</option>
            </select>
          </label>
        </div>
        <div class="d24-pl-note">Aplikacja wykryła płatny odcinek na podstawie rzeczywistej geometrii trasy OSRM. Punkty wjazdu i wyjazdu są dopasowane do najbliższych stacji oficjalnego cennika lub kalkulatora operatora. Możesz poprawić nazwę przed uruchomieniem kalkulatora.</div>
        <div class="d24-pl-actions">
          <button type="button" class="d24-pl-cancel">Anuluj</button>
          <button type="submit" class="d24-pl-submit">Uruchom kalkulator i pobierz zrzut</button>
        </div>
      </form>
    </section>`;
  document.body.appendChild(dialog);
  polishConcessionUi={
    dialog,
    title:dialog.querySelector('#d24PolishConcessionTitle'),
    route:dialog.querySelector('#d24PolishConcessionRoute'),
    form:dialog.querySelector('#d24PolishConcessionForm'),
    entry:dialog.querySelector('#d24PolishConcessionEntry'),
    exit:dialog.querySelector('#d24PolishConcessionExit'),
    entryTextField:dialog.querySelector('#d24PolishConcessionEntryTextField'),
    exitTextField:dialog.querySelector('#d24PolishConcessionExitTextField'),
    entrySelectField:dialog.querySelector('#d24PolishConcessionEntrySelectField'),
    exitSelectField:dialog.querySelector('#d24PolishConcessionExitSelectField'),
    entrySelect:dialog.querySelector('#d24PolishConcessionEntrySelect'),
    exitSelect:dialog.querySelector('#d24PolishConcessionExitSelect'),
    category:dialog.querySelector('#d24PolishConcessionCategory'),
    direction:dialog.querySelector('#d24PolishConcessionDirection'),
  };
  dialog.querySelector('.d24-pl-close').addEventListener('click',closePolishConcessionDialog);
  dialog.querySelector('.d24-pl-cancel').addEventListener('click',closePolishConcessionDialog);
  dialog.addEventListener('click',event=>{if(event.target===dialog)closePolishConcessionDialog();});
  polishConcessionUi.form.addEventListener('submit',submitPolishConcessionDialog);
  return polishConcessionUi;
}

export async function openPolishConcessionDialog(section){
  pendingPolishConcessionSection=section;
  const ui=ensurePolishConcessionDialog();
  const key=section.calculatorKey;
  let savedForLine=null;
  if(['a1-amberone-pl','a2-awsa-pl','a4-sam-pl','hac-hr','azm-hr','bina-istra-hr','autostrade-it','autoroutes-fr'].includes(key)){
    try{savedForLine=await loadLineCalculatorSettings(state.organizationId,state.currentRoute.lineNumber,key);}catch(error){console.warn('[D24 AUTOSTRADA] Parametry linii:',error);}
  }
  const sectionSavedRoute=(savedForLine?.sectionRoutes && typeof savedForLine.sectionRoutes==='object')
    ? (savedForLine.sectionRoutes[String(Number(section.order||0))]||{})
    : {};
  const defaults={
    ...(savedForLine||{}),
    ...sectionSavedRoute,
  };
  const titles={
    'a1-amberone-pl':'A1 — AmberOne Rusocin–Nowa Wieś',
    'a2-awsa-pl':'A2 — Autostrada Wielkopolska',
    'a4-sam-pl':'A4 — Katowice–Kraków',
    'hac-hr':'HAC — płatne autostrady Chorwacji',
    'azm-hr':'A2 — Autostrada Zagreb–Macelj',
    'bina-istra-hr':'Bina-Istra — Istarski ipsilon',
    'autostrade-it':'Autostrade per l’Italia',
    'autoroutes-fr':'Autoroutes.fr — Francja',
  };
  configureMotorwayCategoryOptions(ui.category,key);
  ui.title.textContent=titles[key]||section.displayName||'Dodatkowa opłata autostradowa';
  // Źródłem prawdy są zapisane wartości tej konkretnej linii/sekcji.
  // Automatyczne wykrycie jest używane wyłącznie przy pierwszym uruchomieniu.
  const detectedEntry=defaults.entryName
    || section.tollEntryName
    || section.entryName
    || section.points?.[0]?.name
    || '';
  const detectedExit=defaults.exitName
    || section.tollExitName
    || section.exitName
    || section.points?.at(-1)?.name
    || '';
  const isAmberOne=key==='a1-amberone-pl';
  ui.entryTextField?.classList.toggle('hidden',isAmberOne);
  ui.exitTextField?.classList.toggle('hidden',isAmberOne);
  ui.entrySelectField?.classList.toggle('hidden',!isAmberOne);
  ui.exitSelectField?.classList.toggle('hidden',!isAmberOne);
  ui.entry.required=!isAmberOne;
  ui.exit.required=!isAmberOne;
  ui.entrySelect.required=isAmberOne;
  ui.exitSelect.required=isAmberOne;

  if(isAmberOne){
    const options=A1_AMBERONE_STATIONS.map(name=>`<option value="${esc(name)}">${esc(name)}</option>`).join('');
    ui.entrySelect.innerHTML=options;
    ui.exitSelect.innerHTML=options;
    ui.entrySelect.value=A1_AMBERONE_STATIONS.includes(detectedEntry)?detectedEntry:(section.tollEntryName||A1_AMBERONE_STATIONS[0]);
    ui.exitSelect.value=A1_AMBERONE_STATIONS.includes(detectedExit)?detectedExit:(section.tollExitName||A1_AMBERONE_STATIONS.at(-1));
    ui.entry.value=ui.entrySelect.value;
    ui.exit.value=ui.exitSelect.value;
  }else{
    ui.entry.value=detectedEntry;
    ui.exit.value=detectedExit;
  }

  ui.category.value=key==='azm-hr'
    ? '3'
    : (defaults.vehicleCategory||'3');
  ui.direction.value='one-way';
  const shownEntry=isAmberOne?ui.entrySelect.value:ui.entry.value;
  const shownExit=isAmberOne?ui.exitSelect.value:ui.exit.value;
  ui.route.innerHTML=`<strong>${esc(section.displayName||section.countryName)}:</strong> ${esc(shownEntry)} → ${esc(shownExit)}<br><small>Dystans wykrytego płatnego odcinka: ${sectionDistanceKm(section)??'—'} km.</small>`;
  ui.dialog.classList.remove('hidden');
  ui.dialog.setAttribute('aria-hidden','false');
  document.body.style.overflow='hidden';
  setTimeout(()=>ui.category.focus(),0);
}


export function configureMotorwayCategoryOptions(select,key){
  const groups={
    'a1-amberone-pl':[
      ['2','Autobus 2 osie — A1 kategoria 2'],
      ['3','Autobus 3 osie — A1 kategoria 3'],
      ['4','Autobus 4 osie lub więcej — A1 kategoria 4'],
    ],
    'autostrade-it':[
      ['2','2 osie — włoska klasa B (autobus)'],
      ['3','3 osie — włoska klasa 3'],
      ['4','4 osie — włoska klasa 4'],
      ['5','5 lub więcej osi — włoska klasa 5'],
    ],
    'autoroutes-fr':[
      ['2','Autobus 2-osiowy — francuska klasa 3'],
      ['3','Autobus 3-osiowy — francuska klasa 4'],
      ['4','Autobus 4-osiowy — francuska klasa 4'],
      ['5','Autobus 5-osiowy — francuska klasa 4'],
    ],
  };
  const options=groups[key]||[
    ['2','Kategoria 2 — 2 osie, koła bliźniacze / przyczepa'],
    ['3','Kategoria 3 — autobus lub pojazd 3-osiowy'],
    ['4','Kategoria 4 — więcej niż 3 osie'],
    ['5','Kategoria 5 — pojazd nienormatywny'],
  ];
  select.innerHTML=options.map(([value,label])=>`<option value="${value}">${esc(label)}</option>`).join('');
}

export function closePolishConcessionDialog(){
  pendingPolishConcessionSection=null;
  if(!polishConcessionUi)return;
  polishConcessionUi.dialog.classList.add('hidden');
  polishConcessionUi.dialog.setAttribute('aria-hidden','true');
  document.body.style.overflow='';
}

export async function submitPolishConcessionDialog(event){
  event.preventDefault();
  if(!pendingPolishConcessionSection)return;
  const section=pendingPolishConcessionSection;
  const ui=ensurePolishConcessionDialog();
  if(!ui.form.reportValidity())return;
  const isAmberOne=section.calculatorKey==='a1-amberone-pl';
  const entryName=String(isAmberOne?ui.entrySelect.value:ui.entry.value).trim();
  const exitName=String(isAmberOne?ui.exitSelect.value:ui.exit.value).trim();
  if(isAmberOne&&entryName===exitName){
    return setStatus(els.extensionStatus,'A1 AmberOne: węzeł wjazdu i wyjazdu muszą być różne.','warning');
  }
  const settings={
    system:section.calculatorKey,
    vehicleCategory:ui.category.value,
    direction:'one-way',
    entryName,
    exitName,
  };
  // Ustawienia autostradowe są zapisywane wyłącznie w Supabase
  // dla konkretnej linii i sekcji. Nie korzystamy ze wspólnego localStorage,
  // aby wartości jednej linii nie pojawiały się w innej.
  if(['a1-amberone-pl','a2-awsa-pl','a4-sam-pl','hac-hr','azm-hr','bina-istra-hr','autostrade-it','autoroutes-fr'].includes(section.calculatorKey)){
    try{
      const existingLineSettings=await loadLineCalculatorSettings(
        state.organizationId,
        state.currentRoute.lineNumber,
        section.calculatorKey
      )||{};
      const routeSettings={
        entryName:settings.entryName,
        exitName:settings.exitName,
        roadGroup:section.calculatorKey==='hac-hr'
          ? inferHacRoadGroup(section,{...existingLineSettings,...settings})
          : (section.roadGroup||''),
        roadCode:section.roadCode||'',
        operatorName:section.operatorName||'',
        displayName:section.displayName||'',
        sectionOrder:Number(section.order||0),
      };
      const sectionRoutes={
        ...(existingLineSettings.sectionRoutes&&typeof existingLineSettings.sectionRoutes==='object'
          ? existingLineSettings.sectionRoutes
          : {}),
        [String(Number(section.order||0))]:routeSettings,
      };
      await saveLineCalculatorSettings(state.organizationId,state.currentRoute.lineNumber,section.calculatorKey,{
        ...existingLineSettings,
        system:settings.system,
        vehicleCategory:settings.vehicleCategory,
        direction:settings.direction,
        sectionRoutes,
        // Top-level zachowujemy dla zgodności, ale runner ręczny i nowy automat
        // korzystają z konkretnej sekcji.
        ...routeSettings,
      });
    }catch(error){
      console.error('[D24 AUTOSTRADA] Nie udało się zapisać wjazdu/wyjazdu linii:',error);
      return setStatus(els.extensionStatus,`Nie udało się zapisać punktu wjazdu/wyjazdu dla linii: ${error.message}`,'error');
    }
  }
  closePolishConcessionDialog();
  await runPolishConcession(section,settings);
}

export async function runPolishConcession(section,settings){
  const adjective=section.countryCode==='HR'?'chorwackiego płatnego':section.countryCode==='IT'?'włoskiego autostradowego':section.countryCode==='FR'?'francuskiego autostradowego':'polskiego koncesyjnego';
  const preparedSection=await prepareSectionMetrics(section,adjective);
  if(!preparedSection)return;
  if(!await requireAuthenticatedSession('uruchomić kalkulator autostradowy Browserless'))return;
  if(preparedSection.calculatorKey==='autostrade-it'&&(preparedSection.points||[]).length>3){
    const message=`Autostrade Italia: obecna automatyzacja obsługuje początek, maks. 1 punkt pośredni i cel (3 punkty). Odcinek ma ${preparedSection.points.length}.`;
    window.alert(message);
    return setStatus(els.extensionStatus,message,'warning');
  }
  if(preparedSection.calculatorKey==='autoroutes-fr'&&(preparedSection.points||[]).length>5){
    const message=`Autoroutes.fr obsługuje w tej automatyzacji maksymalnie 5 punktów. Odcinek ma ${preparedSection.points.length}.`;
    window.alert(message);
    return setStatus(els.extensionStatus,message,'warning');
  }

  const distanceKm=sectionDistanceKm(preparedSection);
  const durationText=Number.isFinite(Number(preparedSection.durationSeconds))?formatDuration(Number(preparedSection.durationSeconds)):'';
  const jobId=crypto.randomUUID();
  const payload={
    jobId,
    calculatorKey:preparedSection.calculatorKey,
    routeId:state.currentRoute.id,
    routeVersionId:state.currentRoute.versionId,
    routeTitle:state.currentRoute.title,
    sourceFile:state.currentRoute.sourceFileName,
    countryCode:preparedSection.countryCode||'PL',
    countryName:preparedSection.displayName||preparedSection.countryName||'Polska',
    sectionId:preparedSection.id,
    sectionOrder:preparedSection.order,
    roadCode:preparedSection.roadCode,
    roadGroup:preparedSection.calculatorKey==='hac-hr' ? inferHacRoadGroup(preparedSection, settings) : (preparedSection.roadGroup||''),
    operatorName:preparedSection.operatorName,
    entryName:settings.entryName,
    exitName:settings.exitName,
    distanceKm,
    durationText,
    points:preparedSection.points.map(point=>({
      name:point.name,searchName:point.searchName||point.name,address:point.address||'',countryCode:preparedSection.countryCode||'PL',countryName:preparedSection.countryName||'Polska',
      lat:point.lat,lon:point.lon,isBorder:false,isTollPoint:true,
    })),
    settings:{
      ...settings,
      entryName:settings.entryName,
      exitName:settings.exitName,
      roadGroup:preparedSection.calculatorKey==='hac-hr' ? inferHacRoadGroup(preparedSection, settings) : (preparedSection.roadGroup||''),
      roadCode:preparedSection.roadCode||'',
      operatorName:preparedSection.operatorName||'',
      sectionOrder:Number(preparedSection.order||0),
    },
  };
  setStatus(els.extensionStatus,`Uruchamiam ${preparedSection.roadCode||preparedSection.calculatorKey} ${settings.entryName} → ${settings.exitName} w Browserless…`,'working');
  try{await startCountryBrowserlessJob(payload);}catch(error){console.error('[D24 BROWSERLESS AUTOSTRADA]',error);}
}


